// Bootstraps the singleton JetStream consumer for the user's company
// scope. Mounted ONCE at the AppShell level. Component-lifecycle owns
// only the consumer's start/stop; the underlying NATS connection is the
// module singleton in nats/jsClient.ts and outlives any single mount.
//
// Phase D boot order:
//   1. shouldUseCache() probes IDB availability + quota
//   2. if cache available: getWatermark(scope) — async, cheap meta-only read
//   3. probe getStreamInfo() to learn the lake's actual MaxAge / firstSeq
//   4. if watermark fresh AND inside lake → subscribeStream({ startSeq: wm + 1 })
//      else                                → subscribeStream({ startMs: now - 8h })
//   5. async hydrate(scope) into the store via ingestFromCache(env) (no
//      re-persist; the dedup map collapses the seam against live arrivals)
//   6. for-await drives the rAF-batched ingestQueue + IDB persistence
//   7. evictExpired(scope) on a 5-min cadence

import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { logSafeError } from '@/nats/jsClient';
import { subscribeStream, getStreamInfo, type SubscribeHandle } from '@/nats/api';
import { queueEnvelope, ingestFromCache, clearQueue, setPersistTenant } from '@/nats/ingestQueue';
import { useConnectionStore } from '@/store/connectionStore';
import {
  shouldUseCache,
  getWatermark,
  hydrate,
  evictExpired,
} from '@/idb/cache';

export type JsStatus = 'idle' | 'no-scope' | 'connecting' | 'live' | 'error';

const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;
// Trailing window the live consumer replays on (re)subscribe. The live
// store caps at MAX_HISTORY (~5000 rows ≈ minutes, not hours) and R2 via
// the historian preview/window owns everything older, so replaying the
// full 8h lake was ~95% wasted WS bytes + worker decode — and on
// Safari/iOS (no IndexedDB → no warm-resume) it happened on EVERY load,
// which is the slow-first-paint + fast-forward-stutter cause. 30 min is
// plenty to fill the live tail before the historian backfill lands.
const COLD_REPLAY_MS = 30 * 60 * 1000;
const EVICT_INTERVAL_MS = 5 * 60 * 1000;
// How long a tab can be hidden before we proactively tear down the
// server-side consumer. The reasoning is the comment in nats/api.ts:
// Chrome throttles background timers enough that consume() heartbeats
// keep the ordered consumer "alive" from the server's POV indefinitely,
// so inactive_threshold never fires. Tearing down ourselves while the
// WS is still healthy means consumer.delete() actually lands. On
// re-focus the effect re-runs and warm-resumes from the watermark.
const HIDDEN_GRACE_MS = 60_000;

function initialVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

export function useJsConsumer(enabled: boolean = true): JsStatus {
  const tokenPresent = useAuthStore((s) => !!s.user?.token);
  const company      = useAuthStore((s) => s.company);
  const isAdmin      = useAuthStore((s) => s.user?.isAdmin ?? false);
  const userCo       = useAuthStore((s) => s.user?.companySlug ?? null);
  const sub          = useAuthStore((s) => s.user?.sub ?? '');

  const [status, setStatus]   = useState<JsStatus>('idle');
  const [visible, setVisible] = useState<boolean>(initialVisible);

  // Track tab visibility independently of the stream effect. Flipping
  // `visible` to false after the grace window triggers the stream
  // effect's cleanup, which calls handle.stop() -> consumer.delete()
  // while the WS is still alive. Becoming visible again restarts the
  // stream (effect re-runs because `visible` is a dep).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let stopTimer: number | null = null;
    const apply = () => {
      if (document.visibilityState === 'hidden') {
        if (stopTimer) clearTimeout(stopTimer);
        stopTimer = window.setTimeout(() => setVisible(false), HIDDEN_GRACE_MS);
      } else {
        if (stopTimer) { clearTimeout(stopTimer); stopTimer = null; }
        setVisible(true);
      }
    };
    document.addEventListener('visibilitychange', apply);
    return () => {
      document.removeEventListener('visibilitychange', apply);
      if (stopTimer) clearTimeout(stopTimer);
    };
  }, []);

  useEffect(() => {
    if (!enabled)      { setStatus('idle'); useConnectionStore.getState().setStatus('idle'); return; }
    if (!tokenPresent) { setStatus('idle'); useConnectionStore.getState().setStatus('idle'); return; }
    if (!visible)      { setStatus('idle'); useConnectionStore.getState().setStatus('idle'); return; }
    const scope = company || (isAdmin ? null : userCo);
    // 'no-scope' is distinct from 'idle': we're authed and visible but
    // the live stream literally CAN'T subscribe (admin chose "all
    // companies", or token is missing company_slug). UI uses this to
    // show a "Pick a company" CTA instead of an infinite spinner.
    if (!scope) { setStatus('no-scope'); useConnectionStore.getState().setStatus('no-scope'); return; }

    // The IDB warm cache (rows + watermark) is keyed per USER, not just per
    // company. Two users sharing a browser have the same company `scope` but
    // must not share a cache: the prior user's watermark would warm-resume the
    // new user's consumer to a stream seq it can't satisfy, hanging it at
    // "connecting". NATS subjects stay company-scoped (`scope`); only the
    // local cache namespace carries the user id.
    const cacheScope = sub ? `${scope}::${sub}` : scope;

    let handle: SubscribeHandle | null = null;
    let cancelled = false;
    let evictTimer: number | null = null;
    // pagehide is the modern replacement for beforeunload: it fires for
    // tab close, navigation, bfcache, and mobile-bg cases where
    // beforeunload doesn't. Without this the server-side consumer leaks
    // until its inactive_threshold elapses. The handle.stop() chain
    // calls consumer.delete() on the server — best-effort, since
    // pagehide can't await async work and the WS may already be dying.
    // The real cleanup path is the visibilitychange-driven teardown
    // above; pagehide just covers the abrupt-close edge case.
    const onPageHide = () => {
      if (handle) void handle.stop();
    };
    window.addEventListener('pagehide', onPageHide);
    setStatus('connecting');
    useConnectionStore.getState().setStatus('connecting');

    // Persisted writes from this point on land in lc-cache-${cacheScope}.
    setPersistTenant(cacheScope);

    (async () => {
      try {
        // 1. Cache probe — cheap; resolves to false on Safari/iOS or low quota.
        const cacheOK = await shouldUseCache();

        // 2. Read watermark + stream info in parallel.
        const [wm, info] = await Promise.all([
          cacheOK ? getWatermark(cacheScope) : Promise.resolve(null),
          getStreamInfo().catch((err) => {
            logSafeError('[js] stream info probe failed', err);
            return null;
          }),
        ]);
        if (cancelled) return;

        if (info) {
          useConnectionStore.getState().setLakeInfo(info.maxAgeMs, info.firstTimeMs);
        }

        // 3. Pick start strategy. Both paths are bounded to the trailing
        // COLD_REPLAY_MS so we never pull more than ~30 min of tail:
        //   - warm resume (startSeq) only when the watermark is recent
        //     enough that resuming replays less than the cold window —
        //     a long absence falls through to the time-based start so we
        //     don't replay hours from an old sequence.
        //   - cold start at now-COLD_REPLAY_MS, clamped to the lake's
        //     oldest message so we don't ask for data the broker aged out.
        const lakeFirstMs = info?.firstTimeMs ?? Date.now() - EIGHT_HOURS_MS;
        const coldStartMs = Math.max(Date.now() - COLD_REPLAY_MS, lakeFirstMs);
        const wmRecent = wm != null && wm.serverTimeMs > Date.now() - COLD_REPLAY_MS;
        const startSpec = wmRecent
          ? { startSeq: wm.streamSeq + 1 }
          : { startMs: coldStartMs };

        handle = await subscribeStream({
          filterSubjects: [
            `${scope}.*.*.sensor.>`,
            `${scope}.*.*.logs.>`,
            `${scope}.*.*.alerts.>`,
          ],
          ...startSpec,
        });
        if (cancelled) {
          await handle.stop();
          return;
        }
        setStatus('live');
        useConnectionStore.getState().setStatus('live');

        // 4. Hydrate from IDB in the background. Live tail starts immediately
        // above; cache rows fan in via ingestFromCache (dedup absorbs any
        // overlap with the live arrivals on the seam).
        if (cacheOK) {
          void (async () => {
            try {
              const { envs } = await hydrate(cacheScope);
              if (cancelled) return;
              for (const env of envs) ingestFromCache(env);
            } catch (err) {
              logSafeError('[js] hydrate failed', err);
            }
          })();

          // Boot eviction + a 5-min cadence. Best-effort; failures are logged.
          void evictExpired(cacheScope).catch((err) => logSafeError('[js] evict failed', err));
          evictTimer = window.setInterval(() => {
            void evictExpired(cacheScope).catch((err) => logSafeError('[js] evict failed', err));
          }, EVICT_INTERVAL_MS);
        }

        // 5. Drain the iterator. Every envelope flows through queueEnvelope,
        // which dedupes + adapts + schedules the rAF flush + queues an IDB
        // write.
        for await (const env of handle.messages) {
          if (cancelled) break;
          queueEnvelope(env);
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          useConnectionStore.getState().setStatus('error');
          useConnectionStore.getState().setError(safeErrorMessage(err));
        }
        logSafeError('[js] consumer failed', err);
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onPageHide);
      if (evictTimer != null) clearInterval(evictTimer);
      clearQueue();
      void handle?.stop();
    };
  }, [enabled, tokenPresent, company, isAdmin, userCo, sub, visible]);

  return status;
}

function safeErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message.slice(0, 200);
  return String(err).slice(0, 200);
}
